import { Container, Typography, Link as MaterialLink } from '@material-ui/core';
import { format } from 'date-fns';
import parse, { Element, domToReact } from 'html-react-parser';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import Link from 'next/link';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import AlertPopup from '@components/common/alert-popup';
import Button from '@components/common/button';
import { useGlobalResize, useGlobalScroll } from '@hooks/event-hooks';
import { GuideProps } from '@pages/guides/[id]';
import idNormalize from '@utils/id-normalize';
import useStyles from '@views/guides/guide/styles';

const GuideItemView: React.FC<Required<GuideProps>> = ({ guide }) => {
  const classes = useStyles();

  const [headerArray, setHeaderArray] = useState<string[]>([]);
  const titlesRef = useRef<Array<HTMLButtonElement | null>>([]);
  const [windowWidth, setWindowWidth] = useState(0);

  const [contentArray, setContentArray] = useState<Array<HTMLHeadingElement>>(
    [],
  );

  useEffect(() => {
    if (!windowWidth) setWindowWidth(window.innerWidth);
  }, [windowWidth]);

  const handleScroll = useCallback(() => {
    const closest = [...contentArray].sort((a, b) => {
      const aTop = a.getBoundingClientRect().top;
      const bTop = b.getBoundingClientRect().top;

      if (aTop < 0 && bTop < 0) {
        const absA = Math.abs(aTop);
        const absB = Math.abs(bTop);

        if (absA < absB) return -1;

        if (absA > absB) return 1;

        return 0;
      }

      if (aTop < bTop) return -1;

      if (aTop > bTop) return 1;

      return 0;
    })[0];

    titlesRef?.current?.forEach((title) => {
      if (title) {
        if (title.textContent === closest?.textContent) {
          title.classList.add('active');
        } else {
          title.classList.remove('active');
        }
      }
    });
  }, [contentArray]);

  useEffect(() => {
    handleScroll();
  }, [guide, handleScroll]);

  useGlobalResize(() => setWindowWidth(window.innerWidth));
  useGlobalScroll(handleScroll, [contentArray]);

  const guideContent = useMemo(() => {
    const headingRefs: Array<HTMLHeadingElement> = [];
    const headingTexts: Array<string> = [];

    const content = parse(guide.body, {
      // eslint-disable-next-line react/no-unstable-nested-components
      replace: (domNode) => {
        if (domNode instanceof Element && domNode.name === 'h2') {
          const h2 = (
            <h2
              ref={(ref) => {
                if (ref) headingRefs.push(ref);
              }}
            >
              {domToReact(domNode.children)}
            </h2>
          );

          headingTexts.push(domToReact(domNode.children) as string);

          return h2;
        }

        if (
          domNode instanceof Element &&
          domNode.name === 'a' &&
          domNode.attribs.href.startsWith('/')
        ) {
          return (
            <Link href={domNode.attribs.href} passHref>
              <MaterialLink className="blue">
                {domToReact(domNode.children)}
              </MaterialLink>
            </Link>
          );
        }

        return domNode;
      },
    });

    setContentArray(headingRefs);
    setHeaderArray(headingTexts);

    return content;
  }, [guide]);

  const { title, preview, author, createdAt, next, related } = guide;
  const { firstName, lastName, avatar } = author;

  return (
    <>
      <section className={classes.topSection}>
        <Container maxWidth={false}>
          <Link href="/guides" passHref>
            <MaterialLink id="go-back-button-guide" className="blue">
              <i className="icon-arrow-left" />
              Back
            </MaterialLink>
          </Link>
          <Typography className={classes.guideTitle} variant="h1">
            {title}
          </Typography>
          <Typography className={classes.guidePreview} variant="body2">
            {preview}
          </Typography>
          <div className={classes.authorBlock}>
            <img src={avatar} alt={`${firstName} ${lastName} avatar`} />
            <Typography variant="body1">
              {`${firstName} ${lastName}`}
            </Typography>
            <Typography className={classes.guideDate} variant="body2">
              {format(new Date(createdAt), 'dd MMMM yyyy')}
            </Typography>
          </div>
        </Container>
      </section>
      <section className={classes.guideContent}>
        <Container maxWidth={false}>
          <div className={classes.guideColumnsWrapper}>
            {windowWidth > 960 && (
              <div className={classes.sidebar}>
                <div className={classes.sticky}>
                  {headerArray.map((item, index) => (
                    <button
                      id={`${idNormalize(item)}-sticky-item-guide`}
                      type="button"
                      ref={(el) => {
                        titlesRef.current[index] = el;
                      }}
                      onClick={() => {
                        window.scrollTo({
                          top: contentArray[index]?.offsetTop || 0 + 1, // heck for safari
                          behavior: 'smooth',
                        });
                      }}
                      key={item}
                      className={classes.headerButton}
                    >
                      <Typography variant="body2">{item}</Typography>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className={classes.guideContainer}>{guideContent}</div>
          </div>
        </Container>
      </section>
      <section className={classes.bottomSection}>
        <Container maxWidth={false}>
          {next && (
            <div className={classes.nextTip}>
              <Typography variant="subtitle1">Next tip</Typography>
              <div className={classes.nextTipContent}>
                <Typography variant="body2">{next.title}</Typography>
                <Link
                  href={`/guides/${next.id}-${_.kebabCase(next.title)}`}
                  passHref
                >
                  <MaterialLink className="blue">
                    Read more
                    <i className="icon-arrow-right" />
                  </MaterialLink>
                </Link>
              </div>
            </div>
          )}
          <div className={classes.alertWrapper}>
            <AlertPopup
              icon="information"
              title="Write your will online with Simpwill"
              description={
                <>
                  Our solicitor-approved will writing service can help you write
                  a will online in as little as 15 minutes.
                </>
              }
              classWrapper={classes.alertPopup}
              staticPopup
              button={
                <Button
                  id="alert-button-guide"
                  className={classes.alertButton}
                  styleType="containedWhite"
                  text="Get started today"
                  href="/create-will"
                />
              }
            />
          </div>
          {related && (
            <div className={classes.relatedArticles}>
              <Typography variant="subtitle1">Related articles</Typography>
              <div className={classes.relatedLinks}>
                {related.map((article) => (
                  <Link
                    key={article.title}
                    href={`/guides/${article.id}-${_.kebabCase(article.title)}`}
                    passHref
                  >
                    <MaterialLink
                      id={`${idNormalize(article.title)}-link-related-guid`}
                      className="blue"
                    >
                      {article.title}
                    </MaterialLink>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

export default GuideItemView;
